import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, eur, formatErr } from "@/lib/api";

export default function Checkout() {
  const { items, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: user?.name || "",
    address: "",
    city: "",
    postal: "",
    phone: "",
    nif: "",
    payment_method: "",
    notes: "",
  });

  // delivery: type = "shipping" | "pickup"; region + country + modality_id
  const [delivery, setDelivery] = useState({ type: "", region: "", country: "", modality_id: "" });

  const [payMethods, setPayMethods] = useState({ mbway: null, transfer: null, message: "" });
  const [config, setConfig] = useState({ zones: [], pickup: null, quote_message: "Portes sob consulta" });
  const [quote, setQuote] = useState({ subtotal: 0, total_weight_g: 0, options: [], pickup: null, needs_quote: false, quote_message: "", loading: false, error: "" });

  useEffect(() => {
    api.get("/payment-settings").then(({ data }) => {
      setPayMethods({
        mbway: data.mbway ? { active: true, instructions: data.mbway.instructions || "" } : null,
        transfer: data.transfer ? { active: true, instructions: data.transfer.instructions || "" } : null,
        message: data.payment_page_message || "",
      });
      setForm((f) => {
        if (f.payment_method) return f;
        if (data.mbway?.active) return { ...f, payment_method: "mbway" };
        if (data.transfer?.active) return { ...f, payment_method: "transfer" };
        return f;
      });
    }).catch(() => {});
    api.get("/shipping/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const requestQuote = useCallback(async () => {
    if (items.length === 0 || !delivery.type) return;
    setQuote((q) => ({ ...q, loading: true, error: "" }));
    try {
      const { data } = await api.post("/shipping/quote", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        delivery_type: delivery.type,
        region: delivery.region || null,
        country: delivery.country || null,
      });
      setQuote({ ...data, loading: false, error: "" });
    } catch (e) {
      setQuote((q) => ({ ...q, loading: false, error: formatErr(e.response?.data?.detail) || e.message }));
    }
  }, [items, delivery.type, delivery.region, delivery.country]);

  useEffect(() => { requestQuote(); }, [requestQuote]);

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-4xl brand-text mb-4">O carrinho está vazio</h1>
        <p className="brand-muted mb-8">Adicione alguns produtos antes de finalizar a compra.</p>
        <Link to="/loja" className="btn-primary">Ir à loja</Link>
      </main>
    );
  }

  const isPickup = delivery.type === "pickup";
  const isInternational = delivery.region === "international";
  const selectedOption = quote.options?.find((o) => o.modality_id === delivery.modality_id) || null;
  const shippingCost = isPickup ? 0 : (selectedOption ? selectedOption.price : null);
  const total = shippingCost == null ? null : quote.subtotal + shippingCost;

  const validNif = /^\d{9}$/.test((form.nif || "").replace(/\s+/g, ""));
  const addressOk = isPickup || (form.address && form.city && form.postal);
  const deliveryOk = isPickup ? !!quote.pickup : !!selectedOption;
  const canSubmit = !submitting && !quote.loading && delivery.type && deliveryOk && addressOk && form.name && form.phone && form.payment_method && validNif;

  const selectDelivery = (type, region = "") => {
    setDelivery({ type, region, country: "", modality_id: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!delivery.type) { setErr("Escolha uma modalidade de entrega."); return; }
    if (!validNif) { setErr("O NIF deve ter 9 dígitos válidos."); return; }
    if (!isPickup && !selectedOption) { setErr("Escolha uma modalidade de envio válida."); return; }
    if (!form.payment_method) { setErr("Escolha um método de pagamento."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping_name: form.name,
        shipping_address: form.address,
        shipping_city: form.city,
        shipping_postal: form.postal,
        shipping_phone: form.phone,
        nif: form.nif.replace(/\s+/g, ""),
        payment_method: form.payment_method,
        delivery_type: delivery.type,
        region: delivery.region || null,
        country: delivery.country || null,
        modality_id: delivery.modality_id || null,
        notes: form.notes,
      });
      clear();
      nav(`/pagamento/${data.id}`);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
      setSubmitting(false);
      requestQuote();
    }
  };

  const intlZone = config.zones?.find((z) => z.region === "international");

  return (
    <main className="max-w-6xl mx-auto px-6 lg:px-12 py-16" data-testid="page-checkout">
      <p className="label-sm mb-3">Finalização</p>
      <h1 className="font-serif text-4xl lg:text-5xl brand-text mb-12">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12">
        <form onSubmit={submit} className="space-y-10">
          {/* DELIVERY DESTINATION */}
          <section data-testid="delivery-section">
            <h2 className="font-serif text-2xl brand-text mb-6">Entrega</h2>
            <div className="space-y-3">
              {(config.zones || []).map((z) => (
                <label
                  key={z.id}
                  className={`flex items-start gap-3 p-4 border cursor-pointer ${delivery.type === "shipping" && delivery.region === z.region ? "brand-primary-text border-[#8C6A3D]" : "brand-border"}`}
                  data-testid={`dest-${z.region}`}
                >
                  <input
                    type="radio"
                    name="dest"
                    checked={delivery.type === "shipping" && delivery.region === z.region}
                    onChange={() => selectDelivery("shipping", z.region)}
                    data-testid={`dest-${z.region}-radio`}
                  />
                  <div className="flex-1">
                    <p className="font-medium brand-text">{z.name}</p>
                  </div>
                </label>
              ))}

              {config.pickup && (
                <label
                  className={`flex items-start gap-3 p-4 border cursor-pointer ${isPickup ? "brand-primary-text border-[#8C6A3D]" : "brand-border"}`}
                  data-testid="dest-pickup"
                >
                  <input
                    type="radio"
                    name="dest"
                    checked={isPickup}
                    onChange={() => selectDelivery("pickup")}
                    data-testid="dest-pickup-radio"
                  />
                  <div className="flex-1">
                    <p className="font-medium brand-text">{config.pickup.name}</p>
                    <p className="text-xs brand-muted mt-1">{config.pickup.description}</p>
                    <p className="text-xs brand-primary-text mt-1">0,00 €</p>
                  </div>
                </label>
              )}
            </div>

            {/* International country */}
            {delivery.type === "shipping" && isInternational && (
              <div className="mt-4">
                <label className="label-sm">País de destino</label>
                {intlZone?.countries?.length ? (
                  <select
                    value={delivery.country}
                    onChange={(e) => setDelivery({ ...delivery, country: e.target.value, modality_id: "" })}
                    className="input-line mt-2"
                    data-testid="dest-country-select"
                  >
                    <option value="">— Selecione o país —</option>
                    {intlZone.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <input
                    value={delivery.country}
                    onChange={(e) => setDelivery({ ...delivery, country: e.target.value, modality_id: "" })}
                    className="input-line mt-2"
                    placeholder="País"
                    data-testid="dest-country-input"
                  />
                )}
              </div>
            )}

            {/* Shipping modalities */}
            {delivery.type === "shipping" && (
              <div className="mt-6" data-testid="modalities-section">
                {quote.loading ? (
                  <p className="text-sm brand-muted">A calcular portes...</p>
                ) : quote.needs_quote ? (
                  <div className="p-4 brand-surface border brand-border" data-testid="quote-on-request">
                    <p className="font-medium brand-text">{quote.quote_message || config.quote_message}</p>
                    <p className="text-xs brand-muted mt-1">Não existe uma tarifa configurada para este destino/peso. Contacte-nos para concluir a encomenda.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="label-sm">Modalidade de envio</p>
                    {(quote.options || []).map((o) => (
                      <label
                        key={o.modality_id}
                        className={`flex items-center justify-between gap-3 p-4 border cursor-pointer ${delivery.modality_id === o.modality_id ? "brand-primary-text border-[#8C6A3D]" : "brand-border"}`}
                        data-testid={`modality-${o.modality_id}`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="modality"
                            checked={delivery.modality_id === o.modality_id}
                            onChange={() => setDelivery({ ...delivery, modality_id: o.modality_id })}
                            data-testid={`modality-${o.modality_id}-radio`}
                          />
                          <div>
                            <p className="font-medium brand-text">{o.name}{o.carrier ? ` · ${o.carrier}` : ""}</p>
                            {o.delivery_time && <p className="text-xs brand-muted">{o.delivery_time}</p>}
                            {o.message && <p className="text-xs brand-muted">{o.message}</p>}
                          </div>
                        </div>
                        <span className="brand-text">{eur(o.price)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* SHIPPING DATA */}
          <section>
            <h2 className="font-serif text-2xl brand-text mb-6">{isPickup ? "Dados de contacto" : "Dados de envio"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label-sm">Nome completo</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-line mt-2" data-testid="ship-name" />
              </div>
              {!isPickup && (
                <>
                  <div className="md:col-span-2">
                    <label className="label-sm">Morada</label>
                    <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input-line mt-2" data-testid="ship-address" />
                  </div>
                  <div>
                    <label className="label-sm">Cidade</label>
                    <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-line mt-2" data-testid="ship-city" />
                  </div>
                  <div>
                    <label className="label-sm">Código Postal</label>
                    <input required value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })} className="input-line mt-2" data-testid="ship-postal" />
                  </div>
                </>
              )}
              <div>
                <label className="label-sm">Telefone</label>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-line mt-2" data-testid="ship-phone" />
              </div>
              <div>
                <label className="label-sm">NIF <span style={{ color: "#9E4747" }}>*</span></label>
                <input
                  required
                  inputMode="numeric"
                  pattern="\d{9}"
                  maxLength={9}
                  value={form.nif}
                  onChange={(e) => setForm({ ...form, nif: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                  className="input-line mt-2 font-mono"
                  placeholder="9 dígitos"
                  data-testid="ship-nif"
                />
                {form.nif && !validNif && <p className="text-xs mt-1" style={{ color: "#9E4747" }} data-testid="nif-invalid">NIF inválido.</p>}
              </div>
            </div>
            {isPickup && config.pickup && (
              <p className="text-xs brand-muted mt-4" data-testid="pickup-note">{config.pickup.description}</p>
            )}
          </section>

          {/* PAYMENT */}
          <section>
            <h2 className="font-serif text-2xl brand-text mb-6">Método de pagamento</h2>
            <div className="space-y-3" data-testid="payment-methods">
              {payMethods.mbway && (
                <label className={`flex items-start gap-3 p-4 border cursor-pointer ${form.payment_method === "mbway" ? "brand-primary-text border-[#8C6A3D]" : "brand-border"}`} data-testid="pm-mbway">
                  <input type="radio" name="pm" checked={form.payment_method === "mbway"} onChange={() => setForm({ ...form, payment_method: "mbway" })} data-testid="pm-mbway-radio" />
                  <div>
                    <p className="font-medium brand-text">MB WAY</p>
                    <p className="text-xs brand-muted mt-1">{payMethods.mbway.instructions}</p>
                  </div>
                </label>
              )}
              {payMethods.transfer && (
                <label className={`flex items-start gap-3 p-4 border cursor-pointer ${form.payment_method === "transfer" ? "brand-primary-text border-[#8C6A3D]" : "brand-border"}`} data-testid="pm-transfer">
                  <input type="radio" name="pm" checked={form.payment_method === "transfer"} onChange={() => setForm({ ...form, payment_method: "transfer" })} data-testid="pm-transfer-radio" />
                  <div>
                    <p className="font-medium brand-text">Transferência bancária</p>
                    <p className="text-xs brand-muted mt-1">{payMethods.transfer.instructions}</p>
                  </div>
                </label>
              )}
              {!payMethods.mbway && !payMethods.transfer && (
                <p className="text-sm" style={{ color: "#9E4747" }} data-testid="pm-none">
                  Nenhum método de pagamento disponível. Contacte-nos por email.
                </p>
              )}
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl brand-text mb-4">Notas (opcional)</h2>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-line min-h-[80px]" placeholder="Alguma indicação para a preparação da encomenda?" data-testid="order-notes" />
          </section>

          {err && <p className="text-sm" style={{ color: "#9E4747" }} data-testid="checkout-error">{err}</p>}

          <button type="submit" disabled={!canSubmit} className="btn-primary w-full" data-testid="btn-confirm-order">
            {submitting ? "A processar..." : "CONFIRMAR ENCOMENDA"}
          </button>
        </form>

        <aside className="brand-surface p-8 h-fit sticky top-28" data-testid="order-summary">
          <h3 className="font-serif text-2xl brand-text mb-6">Resumo</h3>
          <div className="space-y-4 mb-6">
            {items.map((it) => (
              <div key={it.product_id} className="flex gap-3">
                <div className="w-14 h-16 brand-bg overflow-hidden flex-shrink-0">
                  <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <p className="text-sm brand-text">{it.name}</p>
                  <p className="text-xs brand-muted">Qtd: {it.quantity}</p>
                </div>
                <p className="text-sm brand-text">{eur(it.price * it.quantity)}</p>
              </div>
            ))}
          </div>
          <div className="border-t brand-border pt-4 space-y-2">
            {delivery.type && (
              <div className="flex justify-between text-sm">
                <span className="brand-muted">Destino</span>
                <span data-testid="sum-dest">{isPickup ? "Levantamento presencial" : (quote.zone_name || "—")}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="brand-muted">Subtotal</span>
              <span data-testid="sum-subtotal">{eur(quote.subtotal || 0)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="brand-muted">Portes {selectedOption ? `(${selectedOption.name})` : ""}</span>
              <span data-testid="sum-shipping">
                {!delivery.type ? "—" : quote.loading ? "..." : isPickup ? eur(0) : (selectedOption ? eur(shippingCost) : (quote.needs_quote ? (quote.quote_message || config.quote_message) : "—"))}
              </span>
            </div>
            {quote.error && <p className="text-xs" style={{ color: "#9E4747" }} data-testid="sum-error">{quote.error}</p>}
            <div className="flex justify-between pt-3 border-t brand-border">
              <span className="label-sm">Total</span>
              <span className="font-serif text-2xl brand-text" data-testid="sum-total">
                {total == null ? "—" : eur(total)}
              </span>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
